// Central app store — a React-context port of the Android build's
// `SadieViewModel.kt`.
//
// It owns navigation, the conversation, the microphone latch, the pronunciation
// drill and the progress snapshot. The tutor, the grammar checker and the
// progress store are all local modules, so every screen keeps working with the
// network switched off — the browser's speech services are the only part that
// may reach out, and only in some browsers.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  isEmbedded,
  isRecognitionSupported,
  startListening as startRecognition,
  stopListening as stopRecognition,
} from './speech';
import {
  applyImport,
  clearProgress,
  downloadExport,
  loadCorrectionsEnabled,
  loadVoiceReplies,
  recordMessage,
  recordPracticeScore,
  recordSessionStarted,
  saveCorrectionsEnabled,
  saveVoiceReplies,
  snapshot,
} from './storage';
import {
  currentVoiceName,
  isCurrentlySpeaking,
  isSupported as ttsSupported,
  loadVoice,
  primeSpeech,
  speak,
  stop as ttsStop,
} from './tts';
import { practicePhrases, scenarios, type PracticePhrase, type Scenario } from './tutor/scenarios';
import { score, type PronunciationResult } from './tutor/pronunciationScorer';
import { TutorEngine } from './tutor/tutorEngine';
import { EMPTY_PROGRESS, type ChatMessage, type ProgressSnapshot, type Tab } from './types';

/** A short pause makes the exchange feel like a conversation rather than an
 *  instant lookup. The work itself is synchronous and local. */
const TUTOR_DELAY_MS = 450;

interface StoreValue {
  // navigation
  tab: Tab;
  selectTab: (next: Tab) => void;
  activeScenario: Scenario | null;
  scenarios: readonly Scenario[];
  startScenario: (scenario: Scenario) => void;
  exitChat: () => void;

  // chat
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  tutorTyping: boolean;
  send: () => void;
  askForHint: () => void;
  correctionsEnabled: boolean;
  toggleCorrections: () => void;

  // speech
  voiceReplies: boolean;
  toggleVoiceReplies: () => void;
  isSpeaking: boolean;
  speakAgain: (text: string) => void;
  stopSpeaking: () => void;
  isListening: boolean;
  toggleMic: () => void;
  speechMessage: string | null;
  dismissSpeechMessage: () => void;
  micSupported: boolean;
  voiceSupported: boolean;
  /** True when the app is running inside an iframe on another site. */
  embedded: boolean;
  /** Which browser voice the tutor speaks with; null until the list has loaded. */
  voiceName: string | null;

  // pronunciation practice
  practiceIndex: number;
  practicePhrase: PracticePhrase;
  practiceResult: PronunciationResult | null;
  nextPhrase: () => void;
  previousPhrase: () => void;
  speakPracticePhrase: () => void;
  startPronunciationCheck: () => void;

  // progress
  progress: ProgressSnapshot;
  resetProgress: () => void;
  exportProgress: () => void;
  importProgress: (file: File) => Promise<void>;
}

const StoreContext = createContext<StoreValue | null>(null);

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error('useStore must be used inside <StoreProvider>');
  return value;
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  // ------------------------------------------------------------- navigation
  const [tab, setTab] = useState<Tab>('talk');
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);

  // ------------------------------------------------------------------- chat
  const engineRef = useRef<TutorEngine>(new TutorEngine());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [tutorTyping, setTutorTyping] = useState(false);
  const [correctionsEnabled, setCorrectionsEnabled] = useState(loadCorrectionsEnabled);

  // ----------------------------------------------------------------- speech
  const [voiceReplies, setVoiceReplies] = useState(loadVoiceReplies);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechMessage, setSpeechMessage] = useState<string | null>(null);
  const micSupported = useMemo(isRecognitionSupported, []);
  const voiceSupported = useMemo(ttsSupported, []);
  const embedded = useMemo(isEmbedded, []);
  const [voiceName, setVoiceName] = useState<string | null>(null);

  // --------------------------------------------------------------- practice
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [practiceResult, setPracticeResult] = useState<PronunciationResult | null>(null);

  // --------------------------------------------------------------- progress
  const [progress, setProgress] = useState<ProgressSnapshot>(EMPTY_PROGRESS);

  // Callbacks reach these from inside speech events, where a captured value
  // would be a turn or two stale.
  const typingRef = useRef(false);
  const correctionsRef = useRef(correctionsEnabled);
  const voiceRef = useRef(voiceReplies);
  const practiceIndexRef = useRef(practiceIndex);
  /** Speech that arrived while the tutor was still replying. */
  const pendingSpeechRef = useRef<string | null>(null);
  const idRef = useRef(0);

  correctionsRef.current = correctionsEnabled;
  voiceRef.current = voiceReplies;
  practiceIndexRef.current = practiceIndex;

  const nextId = () => idRef.current++;

  useEffect(() => {
    setProgress(snapshot());
    // Chrome fills the voice list asynchronously, so the name arrives late.
    void loadVoice().then(() => setVoiceName(currentVoiceName()));
  }, []);

  const refreshProgress = useCallback(() => setProgress(snapshot()), []);

  const stopListening = useCallback(() => {
    stopRecognition();
    setIsListening(false);
    pendingSpeechRef.current = null;
  }, []);

  const speakText = useCallback(
    (text: string, rate?: number) => {
      if (!voiceSupported) return;
      speak(text, {
        rate,
        onStart: () => setIsSpeaking(true),
        onDone: () => setIsSpeaking(false),
      });
    },
    [voiceSupported],
  );

  const stopSpeaking = useCallback(() => {
    ttsStop();
    setIsSpeaking(false);
  }, []);

  // ---------------------------------------------------------------- sending

  const submit = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (text === '' || typingRef.current) return;
      setInput('');
      // The microphone is not touched here: once latched on it stays open until
      // the learner taps it again.
      setMessages((prev) => [...prev, { id: nextId(), role: 'learner', text }]);
      typingRef.current = true;
      setTutorTyping(true);

      window.setTimeout(() => {
        const turn = engineRef.current.respond(text, correctionsRef.current);
        recordMessage(turn.detected);
        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: 'tutor',
            text: turn.text,
            praise: turn.praise,
            corrections: turn.corrections,
            correctedSentence: turn.correctedSentence,
            tip: turn.tip,
          },
        ]);
        typingRef.current = false;
        setTutorTyping(false);
        refreshProgress();
        if (voiceRef.current) speakText(turn.text);

        const queued = pendingSpeechRef.current;
        if (queued !== null) {
          pendingSpeechRef.current = null;
          submitRef.current(queued);
        }
      }, TUTOR_DELAY_MS);
    },
    [refreshProgress, speakText],
  );

  // `submit` re-enters itself when speech queued up during a reply.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const send = useCallback(() => submitRef.current(input), [input]);

  // ------------------------------------------------------------- microphone

  /**
   * Latches the microphone on; the second tap latches it off. In between the
   * learner can pause, think and carry on: every finished utterance is sent as
   * a message and the microphone reopens straight away.
   */
  const toggleMic = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }
    setSpeechMessage(null);
    pendingSpeechRef.current = null;
    setIsListening(true);
    startRecognition(true, {
      onPartial: (partial) => {
        // Anything heard while the tutor is talking is the tutor, coming back
        // in through the microphone.
        if (isCurrentlySpeaking()) return;
        if (!typingRef.current) setInput(partial);
      },
      onResult: (heard) => {
        if (isCurrentlySpeaking()) return;
        // One finished utterance. Held back if the tutor is still composing.
        if (typingRef.current) {
          const queued = pendingSpeechRef.current;
          pendingSpeechRef.current = queued === null ? heard : `${queued} ${heard}`.trim();
          return;
        }
        submitRef.current(heard);
      },
      onError: (message) => {
        setIsListening(false);
        setSpeechMessage(message);
      },
    });
  }, [isListening, stopListening]);

  // --------------------------------------------------------------- actions

  const selectTab = useCallback(
    (next: Tab) => {
      setTab(next);
      // A microphone error from an earlier screen shouldn't follow the learner
      // around, and neither should an open microphone.
      setSpeechMessage(null);
      stopListening();
      if (next === 'progress') refreshProgress();
    },
    [refreshProgress, stopListening],
  );

  const startScenario = useCallback(
    (scenario: Scenario) => {
      primeSpeech();
      stopSpeaking();
      setSpeechMessage(null);
      engineRef.current = new TutorEngine(scenario);
      setActiveScenario(scenario);
      typingRef.current = false;
      setTutorTyping(false);
      setInput('');
      const opener: ChatMessage = { id: nextId(), role: 'tutor', text: engineRef.current.opener() };
      setMessages([opener]);
      recordSessionStarted();
      refreshProgress();
      if (voiceRef.current) speakText(opener.text);
    },
    [refreshProgress, speakText, stopSpeaking],
  );

  const exitChat = useCallback(() => {
    stopSpeaking();
    stopListening();
    setActiveScenario(null);
    refreshProgress();
  }, [refreshProgress, stopListening, stopSpeaking]);

  const askForHint = useCallback(() => {
    if (typingRef.current) return;
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'tutor', text: engineRef.current.hint() },
    ]);
  }, []);

  const toggleVoiceReplies = useCallback(() => {
    setVoiceReplies((prev) => {
      const next = !prev;
      saveVoiceReplies(next);
      if (!next) {
        ttsStop();
        setIsSpeaking(false);
      }
      return next;
    });
  }, []);

  const toggleCorrections = useCallback(() => {
    setCorrectionsEnabled((prev) => {
      const next = !prev;
      saveCorrectionsEnabled(next);
      return next;
    });
  }, []);

  // -------------------------------------------------------------- practice

  const practicePhrase = practicePhrases[practiceIndex]!;

  const speakPracticePhrase = useCallback(() => {
    speakText(practicePhrases[practiceIndexRef.current]!.text, 0.85);
  }, [speakText]);

  const nextPhrase = useCallback(() => {
    setPracticeIndex((i) => (i + 1) % practicePhrases.length);
    setPracticeResult(null);
  }, []);

  const previousPhrase = useCallback(() => {
    setPracticeIndex((i) => (i - 1 + practicePhrases.length) % practicePhrases.length);
    setPracticeResult(null);
  }, []);

  const startPronunciationCheck = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }
    setSpeechMessage(null);
    setPracticeResult(null);
    setIsListening(true);
    // One phrase, one score — this one stays single-shot.
    startRecognition(false, {
      onPartial: () => {},
      onResult: (heard) => {
        setIsListening(false);
        const target = practicePhrases[practiceIndexRef.current]!.text;
        const result = score(target, heard);
        setPracticeResult(result);
        recordPracticeScore(result.score);
        refreshProgress();
      },
      onError: (message) => {
        setIsListening(false);
        setSpeechMessage(message);
      },
    });
  }, [isListening, refreshProgress, stopListening]);

  // -------------------------------------------------------------- progress

  const resetProgress = useCallback(() => {
    clearProgress();
    refreshProgress();
  }, [refreshProgress]);

  const exportProgress = useCallback(() => downloadExport(), []);

  const importProgress = useCallback(
    async (file: File) => {
      const restored = applyImport(await file.text());
      refreshProgress();
      setSpeechMessage(`Restored ${restored} saved values from your backup.`);
    },
    [refreshProgress],
  );

  const value: StoreValue = {
    tab,
    selectTab,
    activeScenario,
    scenarios,
    startScenario,
    exitChat,

    messages,
    input,
    setInput,
    tutorTyping,
    send,
    askForHint,
    correctionsEnabled,
    toggleCorrections,

    voiceReplies,
    toggleVoiceReplies,
    isSpeaking,
    speakAgain: speakText,
    stopSpeaking,
    isListening,
    toggleMic,
    speechMessage,
    dismissSpeechMessage: () => setSpeechMessage(null),
    micSupported,
    voiceSupported,
    embedded,
    voiceName,

    practiceIndex,
    practicePhrase,
    practiceResult,
    nextPhrase,
    previousPhrase,
    speakPracticePhrase,
    startPronunciationCheck,

    progress,
    resetProgress,
    exportProgress,
    importProgress,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
